import ClearIcon from "@mui/icons-material/Clear";
import RotateRightIcon from '@mui/icons-material/RotateRight';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import TranslateIcon from '@mui/icons-material/Translate';
import UnfoldMoreDoubleIcon from '@mui/icons-material/UnfoldMoreDouble';
import { Box, Button, Checkbox, CircularProgress, Container, FormControlLabel, FormGroup, Grid, IconButton, InputAdornment, Skeleton, Slider, Stack, Switch, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom/client";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Box3, MathUtils, Quaternion, Vector3 } from "three";
import { GLTFExporter } from 'three/addons/exporters/GLTFExporter.js';
import ComboBox from "../components/ComboBox";
import CustomDialog from "../components/CustomDialog";
import CustomToolTip from "../components/CustomToolTip";
import LanguagesCheckbox from "../components/LanguagesCheckbox";
import useSnackbar from "../components/snackbar/useSnackBar";
import TranslationTextFields from "../components/TranslationTextFields";
import ZoomSlider from "../components/ZoomSlider";
import DataTable from "../data/DataTable";
import { useApi } from "../hooks/useApi";
import { useLanguage } from "../hooks/useLanguage";
import { API_URL, getRandomInt } from "../utils/lib";
import Element2d from "./helper/Element2d";
import Legend from "./helper/Legend";
import ManualModelPreview from "./ManualModelPreview";
import ModelPreview, { centralizeModel } from "./ModelPreview";
import ScreenshotPreview from "./ScreenshotPreview";

export default function EditModel({ userInfo, version: productVersion, pid: productPropId, id: modelPropId, onClose, fetchThroughProduct, viewStatic = false }) {
  // retrieve the id and pid either through params or props
  const { id: modelId } = useParams();
  const id = modelPropId || modelId;
  const pid = productPropId;
  const [searchParams, setSearchParams] = useSearchParams();
  const version = productVersion ?? searchParams.get("version");
  const versionSearchParam = `${version != null ? `version=${version}` : ''}`

  const location = useLocation();
  const { fromTab } = location.state || {};

  const navigate = useNavigate();

  const [model, setModel] = useState({
    name: "",
    globalId: "",
    length: 100,
    height: 100,
    depth: 100,
    scale: 1.0,
    publicFlag: false,
    object: {
      id: undefined,
      name: ''
    },
    xRotation: 0,
    yRotation: 0,
    zRotation: 0,
    wRotation: 1,
    rotation2D: 0,
    localization: {}
  });

  const [rotation, setRotation] = useState(new Quaternion());

  const isStatic = version != null || viewStatic || (id !== 'new' && !userInfo?.isAdmin && model.ownerId !== userInfo?.ownerId);

  let fetchPrefix = '';
  if (fetchThroughProduct) {
    fetchPrefix += `/product/${pid}`;
  }

  // these states hold the file selected by the user, unused when data is fetched from the server
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFile2d, setSelectedFile2d] = useState(null);
  const [selectedPreviewPicture, setSelectedPreviewPicture] = useState(null);

  // the urls to the resource on the server
  const [url3d, setURL3d] = useState("");
  const [url2d, setURL2d] = useState("");
  const [urlPreview, setURLPreview] = useState("");
  // loading-state for the model
  const [isLoading, setIsLoading] = useState(false);
  // loading-state for the 2d svg
  const [is2dLoading, setIs2dLoading] = useState(false);
  // loading-state for the preview picture
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);

  // these states indicate to the submit-function if files should be deleted on the server
  const [delete3d, setDelete3d] = useState(false);
  const [delete2d, setDelete2d] = useState(false);
  const [deletePreviewPicture, setDeletePreviewPicture] = useState(false);

  // the states for the file-drawers
  const [open3dFileChooser, setOpen3dFileChooser] = useState(false);
  const [open2dFileChooser, setOpen2dFileChooser] = useState(false);
  const [openPreviewPictureChooser, setOpenPreviewPictureChooser] = useState(false);

  // the state for switching between 3d, 2d and thumbnail
  const [toggleOption, setToggleOption] = useState('3d');

  // the states for 3d-model-viewing
  const [manual3d, setManual3d] = useState(false);
  const [manualObject, setManualObject] = useState({ length: 1, height: 1, depth: 1, radius: 1, color: '#ffffff', opacity: 1, type: 'cube' });
  const [profMode, setProfMode] = useState(false);
  const modelRef = {
    meshRef: useRef(),
    controlRef: useRef(),
    sceneRef: useRef(),
    lineRef: useRef()
  }

  // the state for the zoom
  const [zoom, setZoom] = useState(10.0)

  // snackbar for displaying information
  const { openSnackBar, closeSnackBar } = useSnackbar();

  // the exporter for the manual model (exports a binary .glb model)
  const exporter = new GLTFExporter();

  // ref that references all translations
  const translationsRef = useRef();
  // if translations should be shown or not
  const [showTranslations, setShowTranslations] = useState(false);

  // for translation
  const intl = useIntl();
  const api = useApi();
  const { locale } = useLanguage();
  // if all languages should be fetched
  const [allLanguages, setAllLanguages] = useState(false);

  const handleSelectFile3d = (data) => () => {
    setURL3d(`${API_URL}/data/${data.id}/file`);
    setSelectedFile({ ...data, uploaded: false });
    setOpen3dFileChooser(false);
    setDelete3d(false);
    // reset rotation
    setRotation(new Quaternion());
    // reset scale
    setModel(oldModel => ({ ...oldModel, scale: 1.0 }));
  };

  const handleSelectFile2d = (data) => () => {
    setURL2d(`${API_URL}/data/${data.id}/file`);
    setSelectedFile2d({ ...data, uploaded: false });
    setOpen2dFileChooser(false);
    // reset 2d-rotation
    setModel(old => ({ ...old, rotation2D: 0 }));
  };

  const handleSelectPreviewPicture = (data) => () => {
    setURLPreview(`${API_URL}/data/${data.id}/file`);
    setSelectedPreviewPicture({ ...data, uploaded: false });
    setOpenPreviewPictureChooser(false);
  };

  const handleDeleteFile3d = () => {
    setURL3d("");
    setSelectedFile(null);
    setDelete3d(true);
  }

  const handleDeleteFile2d = () => {
    setURL2d("");
    setSelectedFile2d(null);
    setDelete2d(true);
  };

  const handleDeletePreviewPicture = () => {
    setURLPreview("");
    setSelectedPreviewPicture(null);
    setDeletePreviewPicture(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setModel({
      ...model,
      [name]: value,
    });
  };

  function jsonBlob(obj) {
    return new Blob([JSON.stringify(obj)], {
      type: "application/json",
    });
  }

  /**
   * Should be executed when data from the server is fetched.
   * @param {*} data the body of the server-response
   */
  const loadDone = (data) => {
    setRotation(() => {
      const q = new Quaternion(data.xRotation, data.yRotation, data.zRotation, data.wRotation);
      return q;
    });
    setModel(data);
    setManual3d(false);
    if (data.file3d) {
      // the key as a random integer is used to trigger a re-render of ModelPreview
      setURL3d(API_URL + fetchPrefix + `/model/${id}/file?type=3d&key=${getRandomInt(1000)}&${versionSearchParam}`);
      setSelectedFile({ ...data.file3d, uploaded: true });
    } else {
      setURL3d("");
    }
    if (data.file2d) {
      setURL2d(API_URL + fetchPrefix + `/model/${id}/file?type=2d&${versionSearchParam}`);
      setSelectedFile2d({ ...data.file2d, uploaded: true });
    } else {
      setURL2d("");
    }
    if (data.previewPicture) {
      setURLPreview(API_URL + fetchPrefix + `/model/${id}/previewPicture?quality=high&${versionSearchParam}`);
      setSelectedPreviewPicture({ ...data.previewPicture, uploaded: true });
    } else {
      setURLPreview("");
    }
    if (data.file3d) {
      setToggleOption('3d');
    } else if (data.file2d) {
      setToggleOption('2d');
    } else if (data.previewPicture) {
      setToggleOption('preview');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (manual3d) {
      createManualModel();
    } else {
      submitData();
    }
  };

  const submitData = (file3d, file2d) => {
    // hold promises that fulfill when the deletion-request has finished (rejected or resolved)
    let deletion3d = () => new Promise(resolve => resolve());
    let deletion2d = () => new Promise(resolve => resolve());
    let deletionPreview = () => new Promise(resolve => resolve());
    // always check if a file should be deleted (the original response from the server must contain an already set file)
    if (delete3d && model.file3d) {
      deletion3d = () => api
        .delete(`/model/${id}/file?type=3d`)
        .catch(error => {
          console.log(error);
        });
    }
    if (delete2d && model.file2d) {
      deletion2d = () => api
        .delete(`/model/${id}/file?type=2d`)
        .catch(error => {
          console.log(error);
        });
    }
    if (deletePreviewPicture && model.previewPicture) {
      deletionPreview = () => api
        .delete(`/model/${id}/previewPicture`)
        .catch(error => {
          console.log(error);
        });
    }

    const modelObj = { ...model, xRotation: rotation.x, yRotation: rotation.y, zRotation: rotation.z, wRotation: rotation.w, localization: getLanguageFields(model) };
    // field 'name' is not needed for server-communication (done through translations)
    delete modelObj.name;
    if (modelObj.object && !modelObj.object.id) {
      modelObj.object = null;
    }
    if (modelObj.globalId === "") {
      modelObj.globalId = null;
    }

    const formData = new FormData();
    // check if file must be uploaded as multipart, or given as reference
    if (file3d && !file3d.uploaded) {
      formData.append("file3d", file3d, file3d.name);
    } else if (selectedFile && !selectedFile.uploaded) {
      // given as reference
      modelObj.file3d = { ...selectedFile };
    } else {
      // no file or already uploaded
      modelObj.file3d = null;
    }
    // now the same for the 2d-file
    // the selected file has precedence over the generated file2d from a possible manual object
    if (selectedFile2d && !selectedFile2d.uploaded) {
      modelObj.file2d = { ...selectedFile2d };
    } else if (file2d) {
      formData.append("file2d", file2d, file2d.name);
    } else {
      modelObj.file2d = null;
    }
    // and now for the preview-picture
    if (selectedPreviewPicture && !selectedPreviewPicture.uploaded) {
      modelObj.previewPicture = { ...selectedPreviewPicture };
    } else {
      modelObj.previewPicture = null;
    }
    // promise for taking screenshot of preview-picture
    let previewPromise = new Promise(resolve => resolve());
    if (!selectedPreviewPicture) {
      if (file3d || selectedFile) {
        // if there is a 3d-file set, but not a previewPicture, make a screenshot and set the previewPicture
        previewPromise = takeScreenshot(file3d ? file3d : selectedFile);
      } else if (selectedFile2d) {
        // if no 3d-file is set, but a 2d-file, take a screenshot of the 2d-file
        previewPromise = take2dScreenshot(url2d);
      }
    }

    formData.append("model", jsonBlob(modelObj));

    setIsLoading(true);
    if (id === "new") {
      previewPromise
        .then(previewPicture => {
          if (previewPicture) {
            formData.append("preview_picture", previewPicture, "screenshot.jpg")
          }
        })
        .then(() => api.post(`/model/`, formData, {
          headers: {
            Accept: "application/json, text/plain, */*",
            "Content-Type": "multipart/form-data",
          }
        }))
        .then((response) => {
          // handle the response
          setSelectedFile(null);
          setSelectedFile2d(null);
          setSelectedPreviewPicture(null);
          if (onClose) {
            onClose(response.data);
          } else {
            navigate(`/models${fromTab != null ? `?tab=${fromTab}` : ''}`);
          }
        })
        .catch((error) => {
          // handle errors
          console.log(error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      // wait until every deletion has finished before making PUT-request
      deletion3d()
        .then(() => deletion2d())
        .then(() => deletionPreview())
        .then(() => previewPromise)
        .then(previewPicture => {
          if (previewPicture) {
            formData.append("preview_picture", previewPicture, "screenshot.jpg")
          }
        })
        .then(() => api
          .put(`/model/${id}`, formData, {
            headers: {
              Accept: "application/json, text/plain, */*",
              "Content-Type": "multipart/form-data",
            }
          }))
        .then((response) => {
          // handle the response
          if (onClose) {
            onClose(response.data);
          } else {
            navigate(`/models${fromTab != null ? `?tab=${fromTab}` : ''}`);
          }
        })
        .catch((error) => {
          // handle errors
          console.log(error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }

  useEffect(() => {
    if (id !== "new") {
      let fetchURL = fetchPrefix;
      fetchURL += `/model/${id}${version != null ? `?${versionSearchParam}` : ''}?localization=true&lang=${locale}`
      setIsLoading(true);
      api
        .get(fetchURL)
        .then(response => {
          loadDone(response.data);
        })
        .catch(error => {
          console.log(error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [id, versionSearchParam]);

  const handleToggleChange = (event, newToggleOption) => {
    if (newToggleOption !== null) {
      setToggleOption(newToggleOption);
      // set loading-states for 2d and previewPicture
      if (newToggleOption === '2d') {
        if (selectedFile2d) {
          setIs2dLoading(true);
        } else {
          setIs2dLoading(false);
        }
      } else if (newToggleOption === "preview") {
        if (selectedPreviewPicture) {
          setIsPreviewLoading(true);
        } else {
          setIsPreviewLoading(false);
        }
      }
    }
  }

  /**
   * Handles the toggle-action of the manual-mode toggle.
   * @param {*} event 
   * @returns 
   */
  const handleManualToggle = (event) => {
    if (event.target.checked && selectedFile) {
      openSnackBar(intl.formatMessage({ id: "EditModel.manualWarning" }));
      return;
    }
    if (manual3d && !event.target.checked) {
      // reset the selected file
      setURL3d(null);
      setSelectedFile(null);
      // reset the manualObject
      setManualObject({ length: 1, depth: 1, height: 1, radius: 1, color: '#ffffff', opacity: 1 });
      // reset the width, depth and height of the model
      setModel(oldModel => ({ ...oldModel, scale: 1.0, length: 100, depth: 100, height: 100 }));
    } else {
      setModel(oldModel => ({ ...oldModel, scale: 1.0, length: parseInt(1000 * manualObject.length, 10), depth: parseInt(1000 * manualObject.depth, 10), height: parseInt(1000 * manualObject.height, 10) }));
    }
    setManual3d(event.target.checked);
  };

  /**
   * Exports a manual .glb (binary) 3d-model-file and calls submitData
   * with the exported file afterwards.
   */
  const createManualModel = () => {
    exporter.parse(modelRef.meshRef.current, gltf => {
      const file = new Blob([gltf], { type: "model/gltf-binary" });
      file.name = 'custom_object.glb';
      const url = URL.createObjectURL(file);
      setURL3d(url);
      file.uploaded = false;
      file.url = url;
      // indicate that the file was manually created (used while taking screenshot)
      file.manual = true;
      // call submit-data and create the manual-model 2d svg
      submitData(file, createManualModel2DSVG());
    }, (error) => console.log(error), { onlyVisible: true, binary: true });
  };

  /**
   * Creates a 2D model SVG out of the manual-object-data.
   * @returns the 2d svg file as a blob
   */
  const createManualModel2DSVG = () => {
    if (manualObject.type === 'cube') {
      let length = manualObject.length;
      let depth = manualObject.depth;
      const max = Math.max(length, depth);
      length = length / max * 100;
      depth = depth / max * 100;
      const svgText = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 ${length} ${depth}"><path fill="#fff" stroke="#000" d="M.5.5h${length - 1}v${depth - 1}H.5z"/></svg>`;
      const file = new Blob([svgText], { type: 'image/svg+xml' });
      file.name = 'custom_cube.svg';
      return file;
    } else if (manualObject.type === 'cylinder') {
      const radius = 100;
      const svgText = `<svg xmlns="http://www.w3.org/2000/svg" width="${radius * 2}" height="${radius * 2}" fill="none" viewBox="0 0 ${radius * 2} ${radius * 2}"><circle cx="${radius}" cy="${radius}" r="${radius - 0.5}" fill="#fff" stroke="#000"/></svg>`;
      const file = new Blob([svgText], { type: 'image/svg+xml' });
      file.name = 'custom_cylinder.svg';
      return file;
    } else {
      return null;
    }
  };

  /**
   * React to a change of the manual value of the specified type.
   * Should be called in the onChange method of a slider.
   * Updates the manualObject and the model.
   * @param {*} type 
   * @returns 
   */
  const handleManualValueChange = (name, value, modelValue) => {
    setManualObject(old => ({ ...old, [name]: value }));
    setModel(old => ({ ...old, [name]: modelValue }))
  };

  /**
   * Adjust the scale of the model according to the maximum x-z-coordinate.
   * @param {*} maximum 
   */
  const adjustScale = (maximum) => {
    if (model.scale === 1.0) {
      // only change scale, if it is not manually set
      setModel(oldModel => ({ ...oldModel, scale: Number((10.0 / maximum)) }));
    }
  }

  /**
   * Takes a screenshot of the current 3d-model and resolves it.
   * If the parameter file is specified, use the information from it.
   * The parameter file should be specified in case of a manual model.
   * @param {*} file the manual-model-file, if applicable
   * @returns 
   */
  const takeScreenshot = (file) => new Promise(resolve => {
    // create container that takes the screenshot-preview
    const container = document.createElement('div');
    container.id = 'tempscreenshot'
    // set dimensions of screenshot
    container.style.width = '1000px';
    container.style.height = '600px';
    // append container to document
    document.body.appendChild(container);

    // add ScreenshotPreview and give resolve (which is later called by ScreenshotPreview)
    const root = ReactDOM.createRoot(container);
    root.render(<ScreenshotPreview url={file.manual ? file.url : url3d} data={model} fileName={file.manual ? file.name : file?.fileName || ''} contentType={file?.contentType || ''} rotation={rotation} resolve={resolve} />);
  });

  /**
   * Loads the specified image asynchronously
   * @param {*} url the url of the image
   * @returns 
   */
  const loadImage = async (url) => {
    const img = document.createElement('img');
    return new Promise((resolve) => {
      img.src = url;
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
    });
  };

  /**
   * Takes a screenshot of the specified image
   * @param {*} url the url of the image
   * @returns 
   */
  const take2dScreenshot = async (url) => {
    const img = await loadImage(url);
    if (!img) {
      return null;
    }
    const canvas = document.createElement('canvas');
    // this is the image-height and -width
    const canvasWidth = 1000;
    const canvasHeight = 600;
    const ctx = canvas.getContext('2d');
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    // clear canvas
    ctx.rect(0, 0, canvasWidth, canvasHeight);
    ctx.fillStyle = "white";
    ctx.fill();

    const imgWidth = img.width;
    const imgHeight = img.height;
    let scalingWidth = imgWidth;
    let scalingHeight = imgHeight;

    if (model.rotation2D === 90 || model.rotation2D === 270) {
      // if the model is rotated such that width and height are changed,
      // consider effects for scaling-factor
      let buff = scalingWidth;
      scalingWidth = scalingHeight;
      scalingHeight = buff;
    }

    // compute scaling factor to maintain aspect ratio
    const scale = Math.min(canvasWidth / scalingWidth, canvasHeight / scalingHeight);
    // new dimensions after scaling
    const newWidth = imgWidth * scale;
    const newHeight = imgHeight * scale;

    // convert degrees to radians
    const angleInRadians = (model.rotation2D * Math.PI) / 180;

    // move origin to the center of the canvas and apply rotation
    ctx.translate(canvasWidth / 2, canvasHeight / 2);
    ctx.rotate(angleInRadians);

    // draw the image
    ctx.drawImage(img, -newWidth / 2, -newHeight / 2, newWidth, newHeight);

    return await new Promise((resolve) => {
      canvas.toBlob((file) => {
        if (!file) {
          resolve(null);
          return;
        }
        resolve(file);
      }, 'image/jpeg', 1.0);
    });
  };

  /**
   * Triggers the loading screen to become invisible (toVisiblity = true)
   * or visible (toVisibility = false).
   * @param {*} toVisibility 
   * @returns 
   */
  const triggerLoadScreen = (toVisibility) => {
    const loadElement = document.getElementById('loadModelDiv');
    if (!loadElement) return;
    if (toVisibility) {
      loadElement.style.opacity = 0;
      loadElement.style.zIndex = 0;
    } else {
      loadElement.style.opacity = 1;
      loadElement.style.zIndex = 21;
    }
  };

  const textFieldStyle = {
    width: '90%'
  }

  /**
   * The zoom-slider for the grid.
   */
  const zoomSlider = useMemo(() => {
    return <div style={{
      backgroundColor: 'white',
      paddingLeft: '4px',
      paddingRight: '4px',
      height: '80%',
      position: 'absolute',
      left: '10px',
      bottom: '10%',
      zIndex: 20,
      borderRadius: 30,
      border: '1px solid'
    }}>
      <ZoomSlider zoom={zoom} setZoom={setZoom} />
    </div>
  }, [zoom]);

  /**
   * The legend for the grid.
   */
  const gridLegend = useMemo(() => {
    return <div style={{
      position: 'absolute',
      right: '5px',
      bottom: '5px',
      zIndex: 20
    }}>
      <Legend />
    </div>
  });

  const handlePublicChange = (event) => {
    setModel(oldModel => ({ ...oldModel, publicFlag: event.target.checked }));
  };

  /**
   * All controls for creating a manual model.
   */
  const manualModelControls = useMemo(() => {
    return <Box sx={{
      width: {
        xs: '90vw',
        sm: '60vw'
      }
    }} >
      <Stack direction="row" sx={{ alignItems: 'center', mb: 1, width: '100%', justifyContent: 'center' }} >
        <p><b><FormattedMessage id="EditModel.cube" /></b></p>
        <Switch checked={manualObject.type !== "cube"} onChange={event => setManualObject(old => ({ ...old, type: event.target.checked ? "cylinder" : "cube" }))} />
        <p><b><FormattedMessage id="EditModel.cylinder" /></b></p>
      </Stack>
      {manualObject.type === "cube" &&
        <Stack spacing={2} direction="row" sx={{ alignItems: 'center', mb: 1 }}>
          <p style={{ width: '100px' }}><FormattedMessage id="EditModel.length" /></p>
          <Slider
            aria-label="ManualModelLength"
            value={Number(manualObject.length)}
            onChange={(_, newValue) => handleManualValueChange('length', newValue, parseInt(1000 * newValue, 10))}
            min={0.001}
            max={20}
            shiftStep={1}
            step={0.001}
            valueLabelDisplay="auto"
            style={{ width: '100%' }}
          />
          <TextField
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">m</InputAdornment>
              ),
            }}
            variant="outlined"
            size="small"
            name={"length"}
            label={intl.formatMessage({ id: "EditModel.manualLength" })}
            type="number"
            inputProps={{ min: 0.001, step: 0.001 }}
            value={manualObject.length}
            onChange={(event) => {
              handleManualValueChange(event.target.name, event.target.value, parseInt(1000 * event.target.value, 10));
            }}
            sx={{ width: '200px' }}
          />
        </Stack>}
      {manualObject.type === "cube" &&
        <Stack spacing={2} direction="row" sx={{ alignItems: 'center', mb: 1 }}>
          <p style={{ width: '100px' }}><FormattedMessage id="EditModel.depth" /></p>
          <Slider aria-label="ManualModelDepth" value={manualObject.depth} onChange={(_, newValue) => handleManualValueChange('depth', newValue, parseInt(1000 * newValue, 10))}
            min={0.001}
            max={20}
            shiftStep={1}
            step={0.001}
            valueLabelDisplay="auto"
            style={{ width: '100%' }}
          />
          <TextField
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">m</InputAdornment>
              ),
            }}
            variant="outlined"
            size="small"
            name={"depth"}
            label={intl.formatMessage({ id: "EditModel.manualDepth" })}
            type="number"
            inputProps={{ min: 0.001, step: 0.001 }}
            value={manualObject.depth}
            onChange={(event) => {
              handleManualValueChange(event.target.name, event.target.value, parseInt(1000 * event.target.value, 10));
            }}
            sx={{ width: '200px' }}
          />
        </Stack>}
      {manualObject.type === "cylinder" &&
        <Stack spacing={2} direction="row" sx={{ alignItems: 'center', mb: 1 }}>
          <p style={{ width: '100px' }}><FormattedMessage id="EditModel.radius" /></p>
          <Slider
            aria-label="ManualModelRadius"
            value={manualObject.radius}
            onChange={(_, newValue) => {
              const size = parseInt(2000 * newValue, 10);
              setManualObject(old => ({ ...old, radius: newValue, length: 2 * newValue, depth: 2 * newValue }));
              setModel(old => ({ ...old, length: size, depth: size }));
            }}
            min={0.001}
            max={20}
            shiftStep={1}
            step={0.001}
            valueLabelDisplay="auto"
            style={{ width: '100%' }}
          />
          <TextField
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">m</InputAdornment>
              ),
            }}
            variant="outlined"
            size="small"
            name={"radius"}
            label={intl.formatMessage({ id: "EditModel.radius" })}
            type="number"
            inputProps={{ min: 0.001, step: 0.001 }}
            value={manualObject.radius}
            onChange={(event) => {
              const newValue = event.target.value;
              const size = parseInt(2000 * newValue);
              setManualObject(old => ({ ...old, radius: newValue, length: 2 * newValue, depth: 2 * newValue }));
              setModel(old => ({ ...old, length: size, depth: size }));
            }}
            sx={{ width: '200px' }}
          />
        </Stack>}
      <Stack spacing={2} direction="row" sx={{ alignItems: 'center', mb: 1 }} >
        <p style={{ width: '100px' }}><FormattedMessage id="EditModel.height" /></p>
        <Slider
          aria-label="ManualModelHeight"
          value={Number(manualObject.height)}
          onChange={(_, newValue) => handleManualValueChange('height', newValue, parseInt(1000 * newValue, 10))}
          min={0.001}
          max={20}
          shiftStep={1}
          step={0.001}
          valueLabelDisplay="auto"
          style={{ width: '100%' }}
        />
        <TextField
          InputLabelProps={{ shrink: true }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">m</InputAdornment>
            ),
          }}
          variant="outlined"
          size="small"
          name={"height"}
          label={intl.formatMessage({ id: "EditModel.manualHeight" })}
          type="number"
          inputProps={{ min: 0.001, step: 0.001 }}
          value={manualObject.height}
          onChange={(event) => {
            handleManualValueChange(event.target.name, event.target.value, parseInt(1000 * event.target.value, 10));
          }}
          sx={{ width: '200px' }}
        />
      </Stack>
      <Stack spacing={2} direction="row" sx={{ alignItems: 'center', mb: 1, justifyContent: 'space-between' }} >
        <p style={{ width: '100px' }}><FormattedMessage id="EditModel.color" /></p>
        <div style={{ width: '100%' }} ></div>
        <TextField
          type="color"
          value={manualObject.color}
          label={intl.formatMessage({ id: "EditModel.color" })}
          onChange={(event) => setManualObject(old => ({ ...old, color: event.target.value }))}
          size="small"
          sx={{
            width: '200px'
          }}
        />
      </Stack>
      <Stack spacing={2} direction="row" sx={{ alignItems: 'center', mb: 1 }}>
        <p style={{ width: '100px' }}><FormattedMessage id="EditModel.opacity" /></p>
        <Slider
          aria-label="ManualModelOpacity"
          value={Number(manualObject.opacity)}
          onChange={(_, newValue) => setManualObject(old => ({ ...old, opacity: newValue ? newValue : 0.01 }))}
          min={0.01}
          max={1}
          shiftStep={0.1}
          step={0.01}
          valueLabelDisplay="auto"
          style={{ width: '100%' }}
        />
        <TextField
          InputLabelProps={{ shrink: true }}
          variant="outlined"
          size="small"
          name="opacity"
          label={intl.formatMessage({ id: "EditModel.opacity" })}
          type="number"
          inputProps={{ min: 0.01, step: 0.01, max: 1 }}
          value={manualObject.opacity}
          onChange={(event) => {
            setManualObject(old => ({ ...old, opacity: event.target.value }))
          }}
          sx={{ width: '200px' }}
        />
      </Stack>
    </Box>
  }, [manualObject, handleManualValueChange]);

  /**
   * The preview-element that is displayed up top.
   * Either a 3d-model, the 2d-representation or the preview-picture.
   */
  const previewElement = useMemo(() => {
    if (toggleOption === '3d') {
      if (manual3d) {
        return (
          <div style={{ width: '100%', height: '100%' }}>
            {zoomSlider}
            {gridLegend}
            <ManualModelPreview model={manualObject} scale={1.0} ref={modelRef.meshRef} zoomValue={zoom} />
          </div>
        )
      } else {
        return (
          <div style={{ width: '100%', height: '100%' }}>
            {/* div for covering up loading-time of 3d-model */}
            <div id="loadModelDiv" style={{ position: 'absolute', width: 'calc(100% - 6px)', height: '99%', left: '2px', top: '2px', backgroundColor: 'white', zIndex: 21, borderRadius: 3, transition: 'opacity 0.8s ease-in, z-index 0.8s step-end' }}>
            </div>
            {url3d && zoomSlider}
            {url3d && gridLegend}
            {url3d && <ModelPreview url={url3d} data={model} fileName={selectedFile?.fileName || ''} contentType={selectedFile?.fileFormat || ''} adjustScale={adjustScale} zoomValue={zoom} ref={modelRef} rotation={rotation} onLoad={triggerLoadScreen} />}
          </div>
        )
      }
    } else if (toggleOption === '2d') {
      return (
        <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }} >
          {is2dLoading && <Skeleton variant="rectangular" width="100%" height="95%" />}
          {url2d && <Element2d is2dLoading={is2dLoading} url2d={url2d} toggleOption={toggleOption} setIs2dLoading={setIs2dLoading} rotation={model.rotation2D} fileFormat={selectedFile2d?.fileFormat} />}
          {url2d && <div style={{ display: 'flex', justifyContent: 'center', width: '100%', height: '5%', alignItems: 'center', color: 'grey' }} > <UnfoldMoreDoubleIcon style={{ transform: 'rotate(90deg)' }} /> <p> &nbsp; <FormattedMessage id="EditModel.length" /></p></div>}
          {url2d && <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', position: 'absolute', color: 'grey', right: '10px', width: '10%' }} ><UnfoldMoreDoubleIcon /> <p style={{ textWrap: 'nowrap' }}> &nbsp; <FormattedMessage id="EditModel.depth" /></p></div>}
        </div>
      )
    } else if (toggleOption === 'preview') {
      return (
        <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
          {isPreviewLoading && <Skeleton variant="rectangular" width="100%" height="100%" />}
          <img onLoad={() => setIsPreviewLoading(false)} height='100%' src={urlPreview} alt={urlPreview} style={{ display: isPreviewLoading ? 'none' : 'block', maxWidth: '100%', objectFit: 'contain' }} />
        </div>
      )
    }
    return;
  }, [toggleOption, url3d, url2d, urlPreview, model, selectedFile, manual3d, manualObject, zoom, rotation, is2dLoading, isPreviewLoading]);

  /**
     * Returns an object with key-value-pairs of translations.
     * If showTranslations = true, the specific translations are used.
     * If showTranslations = false, only uses the current locale.
     * @param {*} data 
     * @returns 
     */
  const getLanguageFields = (data) => {
    if (showTranslations) {
      // translationsRef is used
      if (translationsRef.current) {
        return translationsRef.current.getTranslations();
      } else {
        return {};
      }
    } else {
      // translationsRef is not used
      return { ...data.localization }
    }
  };

  /**
   * Sets the language-field of the current locale.
   * data.name and data.name<Language> is updated.
   * @param {*} value 
   */
  const setLocaleField = (value) => {
    setModel(old => {
      const o = { ...old };
      o.localization[locale] = value;
      o.name = value;
      return o;
    });
  };

  const inferSize = () => {
    if (modelRef?.meshRef?.current) {
      const box = new Box3().setFromObject(modelRef.meshRef.current);
      const size = new Vector3();

      box.getSize(size);

      let length = Number(model.length);
      let height = Number(model.height);
      let depth = Number(model.depth);

      let counter = 0;
      if (length) {
        counter++;
      }
      if (height) {
        counter++;
      }
      if (depth) {
        counter++;
      }

      if (counter !== 1) {
        openSnackBar(intl.formatMessage({ id: "EditModel.inferSizeFail" }));
        return;
      }

      if (model.length) {
        let factorHeight = size.y / size.x;
        height = factorHeight * length;
        let factorDepth = size.z / size.x;
        depth = factorDepth * length;
      } else if (model.height) {
        let factorLength = size.x / size.y;
        length = factorLength * height;
        let factorDepth = size.z / size.y;
        depth = factorDepth * height;
      } else if (model.depth) {
        let factorLength = size.x / size.z;
        length = factorLength * depth;
        let factorHeight = size.y / size.z;
        height = factorHeight * depth;
      }

      length = Math.floor(length);
      height = Math.floor(height);
      depth = Math.floor(depth);

      setModel(oldModel => ({
        ...oldModel,
        length, height, depth
      }));
    } else {
      openSnackBar(intl.formatMessage({ id: "EditModel.inferSizeNoModel" }));
    }
  };

  return (
    <Container maxWidth='xl' >
      {isLoading && <Box
        sx={{
          marginTop: 3,
          marginBottom: 3,
          position: "fixed",
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: "center",
          alignItems: "center",
          zIndex: 10
        }}
      >
        <CircularProgress color="primary" />
      </Box>}
      <Typography variant="h4" gutterBottom sx={{ marginTop: 2 }}>
        <FormattedMessage id="EditModel.heading" />
      </Typography>
      <CustomDialog
        title={intl.formatMessage({ id: "EditModel.3dModel" })}
        content={<DataTable selectable={true} usageFlags={{ model: true, other: true }} onSelect={handleSelectFile3d} />}
        isOpen={open3dFileChooser}
        handleAccept={() => setOpen3dFileChooser(false)}
        handleClose={() => setOpen3dFileChooser(false)}
        maxWidth={"xl"}
        hideConfirmation={true}
      />
      <CustomDialog
        title={intl.formatMessage({ id: "EditModel.2dModel" })}
        content={<DataTable selectable={true} usageFlags={{ svg: true, image: true }} onSelect={handleSelectFile2d} />}
        isOpen={open2dFileChooser}
        handleAccept={() => setOpen2dFileChooser(false)}
        handleClose={() => setOpen2dFileChooser(false)}
        maxWidth={"xl"}
        hideConfirmation={true}
      />
      <CustomDialog
        title={intl.formatMessage({ id: "EditModel.chooseThumbnail" })}
        content={<DataTable selectable={true} usageFlags={{ image: true }} onSelect={handleSelectPreviewPicture} />}
        isOpen={openPreviewPictureChooser}
        handleAccept={() => setOpenPreviewPictureChooser(false)}
        handleClose={() => setOpenPreviewPictureChooser(false)}
        maxWidth={"xl"}
        hideConfirmation={true}
      />
      <Box
        component="form"
        onSubmit={handleSubmit}
      >
        <Grid container alignItems='center' sx={{ marginBottom: 6, width: '100%' }}>
          <Grid item xs={12} >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}
            >
              <Box
                sx={{
                  height: 600,
                  width: '95%',
                  borderRadius: 3,
                  marginTop: '10px',
                  marginBottom: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid',
                  padding: '4px',
                }}
                style={{ position: 'relative' }}
              >
                {previewElement}
              </Box>
              {!isStatic && toggleOption === '3d' && !manual3d && url3d && <Box sx={{
                width: {
                  xs: '90vw',
                  sm: '60vw',
                },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                marginBottom: 2
              }}>
                <FormControlLabel control={<Switch
                  checked={profMode}
                  onChange={event => setProfMode(event.target.checked)}
                  inputProps={{ 'aria-label': 'controlled' }}
                />}
                  label={<p style={{ width: '100px' }} ><b>{intl.formatMessage({ id: "Material.profSettingsLabel" })}</b></p>}
                  labelPlacement="start"
                  sx={{ margin: 0 }} />
                {profMode && <Stack spacing={2} direction="row" sx={{
                  alignItems: 'center', mb: 1,
                  width: {
                    xs: '80%',
                    sm: '80%',
                    md: '60%'
                  }
                }}>
                  <p style={{ width: '100px', textAlign: '' }}><FormattedMessage id="EditModel.scale" /></p>
                  <Slider aria-label="ModelScale" value={model.scale} onChange={(_, newValue) => {
                    setModel(old => ({ ...old, scale: newValue }));
                  }}
                    min={0.1}
                    max={50}
                    shiftStep={1}
                    step={0.01}
                    valueLabelDisplay="auto"
                    style={{ width: '100%' }}
                  />
                </Stack>}
                {
                  profMode &&
                  <Grid container direction="row" justifyContent="center" alignItems="center" spacing={2} >
                    <Grid item container xs={12} sm={4} justifyContent="center" >
                      <Button variant="outlined" startIcon={<RotateRightIcon />}
                        onClick={() => {
                          // axis to rotate around
                          const xAxis = new Vector3(1, 0, 0);
                          if (modelRef.meshRef.current) {
                            modelRef.meshRef.current.rotateOnWorldAxis(xAxis, MathUtils.degToRad(90));
                            centralizeModel(modelRef.meshRef.current, modelRef.lineRef, modelRef.sceneRef.current, model, intl);
                            setRotation(old => {
                              const q = new Quaternion();
                              q.setFromAxisAngle(xAxis, MathUtils.degToRad(90));
                              return q.multiply(old.clone());
                            });
                          }
                        }}
                        sx={{
                          color: "red", // Red text color
                          borderColor: "red", // Red border for outlined button
                          "&:hover": {
                            borderColor: "darkred", // Darker red on hover
                          },
                        }}
                      ><FormattedMessage id="EditModel.rotateX" /></Button>
                    </Grid>
                    <Grid item container xs={12} sm={4} justifyContent="center" >
                      <Button variant="outlined" startIcon={<RotateRightIcon />}
                        onClick={() => {
                          // axis to rotate around
                          const yAxis = new Vector3(0, 1, 0);
                          if (modelRef.meshRef.current) {
                            modelRef.meshRef.current.rotateOnWorldAxis(yAxis, MathUtils.degToRad(90));
                            centralizeModel(modelRef.meshRef.current, modelRef.lineRef, modelRef.sceneRef.current, model, intl);
                            setRotation(old => {
                              const q = new Quaternion();
                              q.setFromAxisAngle(yAxis, MathUtils.degToRad(90));
                              return q.multiply(old.clone());
                            });
                          }
                        }}
                        sx={{
                          color: "green", // Green text color
                          borderColor: "green", // Green border for outlined button
                          "&:hover": {
                            borderColor: "darkgreen", // Darker green on hover
                          },
                        }}
                      ><FormattedMessage id="EditModel.rotateY" /></Button>
                    </Grid>
                    <Grid item container xs={12} sm={4} justifyContent="center" >
                      <Button variant="outlined" startIcon={<RotateRightIcon />}
                        onClick={() => {
                          // axis to rotate around
                          const zAxis = new Vector3(0, 0, 1);
                          if (modelRef.meshRef.current) {
                            modelRef.meshRef.current.rotateOnWorldAxis(zAxis, MathUtils.degToRad(90));
                            centralizeModel(modelRef.meshRef.current, modelRef.lineRef, modelRef.sceneRef.current, model, intl);
                            setRotation(old => {
                              const q = new Quaternion();
                              q.setFromAxisAngle(zAxis, MathUtils.degToRad(90));
                              return q.multiply(old.clone());
                            });
                          }
                        }}
                        sx={{
                          color: "blue", // Blue text color
                          borderColor: "blue", // Blue border for outlined button
                          "&:hover": {
                            borderColor: "darkblue", // Darker blue on hover
                          },
                        }}
                      ><FormattedMessage id="EditModel.rotateZ" /></Button>
                    </Grid>
                    <Grid item container xs={12} justifyContent="center" >
                      <Button variant="outlined" startIcon={<SettingsBackupRestoreIcon />}
                        onClick={() => {
                          // reset rotation by resetting quaternion
                          if (modelRef.meshRef.current) {
                            const q = new Quaternion();
                            modelRef.meshRef.current.setRotationFromQuaternion(q);
                            centralizeModel(modelRef.meshRef.current, modelRef.lineRef, modelRef.sceneRef.current, model, intl);
                            setRotation(q);
                          }
                        }}
                        sx={{
                          color: "black", // Black text color
                          borderColor: "black", // Black border for outlined button
                          "&:hover": {
                            borderColor: "gray", // Gray on hover
                          },
                        }}
                      ><FormattedMessage id="EditModel.resetRotation" /></Button>
                    </Grid>
                  </Grid>
                }
              </Box>}
              {!isStatic && toggleOption === '3d' && manual3d && manualModelControls}
              {!isStatic && toggleOption === '2d' && url2d && <Button
                variant="outlined"
                startIcon={<RotateRightIcon />}
                onClick={() => {
                  setModel(old => ({ ...old, rotation2D: (old.rotation2D + 90) % 360 }))
                }}
                sx={{
                  marginBottom: 1
                }}
              ><FormattedMessage id="EditModel.rotate" />
              </Button>}

              <ToggleButtonGroup
                color="primary"
                value={toggleOption}
                exclusive
                onChange={handleToggleChange}
                aria-label="Platform">
                <ToggleButton value="3d">3D</ToggleButton>
                <ToggleButton value="2d">2D</ToggleButton>
                <ToggleButton value="preview"><FormattedMessage id="EditModel.thumbnail" /></ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Grid>
        </Grid>

        <Grid container spacing={3} alignItems="center" sx={{ border: '1px dashed black', borderRadius: 3, width: "100%", paddingBottom: 2 }}>
          {
            showTranslations &&
            <TranslationTextFields ref={translationsRef} defaultTranslations={{ ...model.localization }}
              isStatic={isStatic} />
          }
          {
            !showTranslations &&
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                required
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                size="small"
                name={"name"}
                label={intl.formatMessage({ id: "EditModel.name" })}
                type="text"
                sx={textFieldStyle}
                value={model.name || ''}
                onChange={(event) => setLocaleField(event.target.value)}
                disabled={isStatic}
                inputProps={{ maxLength: 50 }}
              />
            </Grid>
          }
          {
            <Grid item xs={12} container alignItems={'center'}>
              <CustomToolTip
                tooltip={showTranslations ? <FormattedMessage id='EditObject.hideTranslations' /> : <FormattedMessage id='EditObject.showTranslations' />}
                placement={"bottom"}
              >
                <Button
                  onClick={() => setShowTranslations(old => {
                    if (old) {
                      // update localization if switching from multi-translation-text-fields to single field
                      if (translationsRef.current) {
                        const localization = translationsRef.current.getTranslations();
                        setModel(data => ({ ...data, name: localization[locale], localization }));
                      }
                    }
                    return !old;
                  })}
                  aria-label='show translations'
                >
                  <TranslateIcon fontSize='large' />
                </Button>
              </CustomToolTip>
            </Grid>
          }
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              size="small"
              name={"globalId"}
              label={intl.formatMessage({ id: "EditModel.globalId" })}
              type="text"
              value={model.globalId != null ? model.globalId : ""}
              onChange={(event) => {
                handleInputChange(event);
              }}
              sx={textFieldStyle}
              disabled={isStatic}
              inputProps={{ maxLength: 50 }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} >
            <TextField
              InputLabelProps={{ shrink: true }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">mm</InputAdornment>
                ),
              }}
              variant="outlined"
              size="small"
              name={"length"}
              label={intl.formatMessage({ id: "EditModel.length" })}
              type="number"
              value={model.length === null ? '' : model.length}
              onChange={(event) => {
                handleInputChange(event);
              }}
              sx={textFieldStyle}
              disabled={isStatic || manual3d}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} >
            <TextField
              InputLabelProps={{ shrink: true }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">mm</InputAdornment>
                ),
              }}
              variant="outlined"
              size="small"
              name={"height"}
              label={intl.formatMessage({ id: "EditModel.height" })}
              type="number"
              value={model.height === null ? '' : model.height}
              onChange={(event) => {
                handleInputChange(event);
              }}
              sx={textFieldStyle}
              disabled={isStatic || manual3d}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} >
            <TextField
              InputLabelProps={{ shrink: true }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">mm</InputAdornment>
                ),
              }}
              variant="outlined"
              size="small"
              name={"depth"}
              label={intl.formatMessage({ id: "EditModel.depth" })}
              type="number"
              value={model.depth === null ? '' : model.depth}
              onChange={(event) => {
                handleInputChange(event);
              }}
              sx={textFieldStyle}
              disabled={isStatic || manual3d}
            />
          </Grid>
          {
            !isStatic && <Grid item xs={12} sm={6} md={3} >
              <Button variant="contained" sx={textFieldStyle} onClick={inferSize}>
                <FormattedMessage id="EditModel.inferSize" />
              </Button>
            </Grid>
          }
          <Grid item xs={12} sm={6} md={4} >
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
              <ComboBox
                label={intl.formatMessage({ id: "EditModel.objectType" })}
                selection={model.object}
                onSelect={(modelType => {
                  setModel(existingValues => ({
                    ...existingValues,
                    object: modelType
                  }))
                })}
                required={false}
                url={`/object/all?published=true&all=true&allLanguages=${allLanguages}&lang=${locale}`}
                style={textFieldStyle}
                idTag="object"
                disabled={isStatic}
              />
              {!isStatic && <LanguagesCheckbox initialValue={allLanguages} onChange={setAllLanguages} disabled={isStatic} style={{ marginRight: '10px' }} />}
            </div>
          </Grid>
          {!isStatic && userInfo?.isAdmin && <Grid item xs={12} sm={12} md={12}>
            <FormControlLabel control={<Checkbox checked={model.publicFlag} onChange={handlePublicChange} />} label={intl.formatMessage({ id: "Edit.published" })} />
          </Grid>}
          {!isStatic && <Grid item xs={12}>
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch checked={manual3d}
                    onChange={handleManualToggle}
                    inputProps={{ 'aria-label': 'controlled' }}
                  />
                }
                label={intl.formatMessage({ id: "EditModel.manualModel" })} />
            </FormGroup>
          </Grid>}
          {!isStatic && !manual3d && <Grid item xs={12}>
            <Button sx={{ width: '30vw' }} variant="outlined" onClick={() => setOpen3dFileChooser(true)} >{intl.formatMessage({ id: "EditModel.3dModel" })}</Button>
            {selectedFile && (
              <IconButton aria-label="clear" color="error" onClick={handleDeleteFile3d}>
                <ClearIcon />
              </IconButton>
            )}
            <Typography variant="subtitle1" ><FormattedMessage id="EditModel.supported3dFormats" /></Typography>
          </Grid>}
          {!isStatic && <Grid item xs={12}>
            <Button sx={{ width: '30vw' }} variant="outlined" onClick={() => setOpen2dFileChooser(true)} >{intl.formatMessage({ id: "EditModel.2dModel" })} (SVG / JPG / PNG / WEBP)</Button>
            {selectedFile2d && (
              <IconButton aria-label="clear" color="error" onClick={handleDeleteFile2d}>
                <ClearIcon />
              </IconButton>
            )}
          </Grid>}
          {!isStatic && <Grid item xs={12}>
            <Button sx={{ width: '30vw' }} variant="outlined" onClick={() => setOpenPreviewPictureChooser(true)} >{intl.formatMessage({ id: "EditModel.chooseThumbnail" })} (JPG / PNG / WEBP)</Button>
            {selectedPreviewPicture && (
              <IconButton aria-label="clear" color="error" onClick={handleDeletePreviewPicture}>
                <ClearIcon />
              </IconButton>
            )}
          </Grid>}
        </Grid>

        {!isStatic && <Button sx={{ marginTop: 2, marginBottom: 2 }} variant="contained" type="submit">
          <FormattedMessage id="Edit.save" />
        </Button>}
      </Box>
    </Container>
  );
}
